import { z } from "zod";

import { listQuerySchema } from "../common/list-query";
import { paginatedSchema } from "../common/pagination";
import { tenantScopeSchema } from "../common/tenant";
import { alertMetricSchema, severitySchema } from "../incidents/incidents";

/**
 * A ceiling on addresses per rule, so a rule cannot become a mailing list.
 *
 * Fan-out is the expensive direction: every recipient is one more message per
 * incident, and an incident storm multiplies by this number before the
 * notification budget gets a chance to stop it.
 */
export const MAX_RECIPIENTS = 20;

/** Shortest wait before an unacknowledged incident may be called. */
export const VOICE_WAIT_MIN_MINUTES = 5;

/** Same one-day ceiling as the other rule timers. */
export const VOICE_WAIT_MAX_MINUTES = 1_440;

/** Suggested wait when the call channel is turned on. */
export const VOICE_WAIT_DEFAULT_MINUTES = 15;

/**
 * Addresses a rule notifies, normalized on the way in.
 *
 * Lowercased before deduplication, which is technically stricter than the
 * standard allows — the local part of an address is case-sensitive on paper —
 * and correct in practice for every provider anyone here will use. Without
 * it, `Ops@acme.com` and `ops@acme.com` are two recipients and the same
 * person gets two copies of every alert.
 */
export const recipientsSchema = z
  .array(z.string().trim().toLowerCase().pipe(z.email().max(254)))
  .max(MAX_RECIPIENTS)
  .transform((values) => [...new Set(values)]);

export const alertRuleSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  organizationName: z.string(),
  /** Null for a rule that covers every device in the organization. */
  deviceId: z.uuid().nullable(),
  devEui: z.string().nullable(),
  assetName: z.string().nullable(),

  metric: alertMetricSchema,
  /**
   * A bound the rule carries itself, which overrides the asset's target range
   * when present (ADR-048). Null means "defer to the asset".
   */
  minValue: z.number().nullable(),
  maxValue: z.number().nullable(),
  durationSeconds: z.number().int().nonnegative(),
  cooldownSeconds: z.number().int().nonnegative(),
  severity: severitySchema,
  enabled: z.boolean(),
  /**
   * Deliberately looser on read than on write, the same asymmetry as the
   * audit trail and the hardware registry (ADR-050). Rules predate this
   * endpoint and were seeded as plain strings; a row holding something that
   * is not a valid address must still read back so an administrator can see
   * it and fix it, rather than failing validation and hiding the problem.
   */
  recipients: z.array(z.string()),
  /** Contacts with current WhatsApp consent selected for this rule. */
  whatsappContactIds: z.array(z.uuid()),
  /** Contacts with current call consent selected for this rule. */
  voiceContactIds: z.array(z.uuid()),
  /** Null when this rule does not escalate to a call. */
  voiceWaitMinutes: z.number().int().nullable(),

  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type AlertRule = z.infer<typeof alertRuleSchema>;

/**
 * A day, as the ceiling for both timers (ADR-091).
 *
 * `durationSeconds` longer than this would mean a condition has to hold for
 * more than a day before anyone hears about it, by which point the stock is
 * already gone; `cooldownSeconds` longer would silence a rule for a day after
 * one alert.
 */
export const RULE_TIMER_MAX_SECONDS = 86_400;

/**
 * The shortest window a rule may require (ADR-091).
 *
 * The same window decides when an incident resolves, and a zero-length one
 * finds no reading inside it to contradict "back in range", so the incident
 * would close on the next uplink while the fault is still there.
 */
export const RULE_DURATION_MIN_SECONDS = 60;

/**
 * The shortest silence that counts as offline (ADR-091).
 *
 * Three reporting intervals of five minutes (ADR-094); a shorter window flags
 * a healthy sensor the first time LoRaWAN drops an uplink. Enforced by the
 * service, because the contract does not know the rule's metric.
 */
export const CONNECTIVITY_DURATION_MIN_SECONDS = 900;

/**
 * Metrics whose timers live on each controller channel rather than on the
 * rule (ADR-082). Their rules carry recipients only, so the service refuses
 * timers for them instead of storing values the engine never reads.
 */
export const CHANNEL_TIMED_METRICS = ["CHANNEL_RANGE", "CHANNEL_ACTIVE"] as const;

/**
 * Metrics a rule can carry its own numeric bounds for. The rest are bit,
 * schedule, silence or channel rules, where a min/max means nothing, so
 * creating one with bounds is refused (ADR-092).
 */
export const RULE_BOUNDED_METRICS = ["TEMPERATURE", "HUMIDITY", "BATTERY", "DISTANCE"] as const;

const ruleDurationSchema = z
  .number()
  .int()
  .min(RULE_DURATION_MIN_SECONDS)
  .max(RULE_TIMER_MAX_SECONDS);
const ruleCooldownSchema = z.number().int().min(0).max(RULE_TIMER_MAX_SECONDS);

const contactIdListSchema = z
  .array(z.uuid())
  .max(MAX_RECIPIENTS)
  .transform((values) => [...new Set(values)]);

const voiceWaitSchema = z
  .number()
  .int()
  .min(VOICE_WAIT_MIN_MINUTES)
  .max(VOICE_WAIT_MAX_MINUTES)
  .nullable();

/**
 * Channel switches. A selected channel with an empty or invalid payload is
 * rejected. An unselected channel must not carry values. Omitting the switch
 * leaves that channel to the legacy fields (email via `recipients`).
 */
const channelSelectionSchema = {
  notifyEmail: z.boolean().optional(),
  notifyWhatsapp: z.boolean().optional(),
  notifyVoice: z.boolean().optional(),
  whatsappContactIds: contactIdListSchema.optional(),
  voiceContactIds: contactIdListSchema.optional(),
  voiceWaitMinutes: voiceWaitSchema.optional(),
};

function refineChannelSelection(
  value: {
    notifyEmail?: boolean | undefined;
    notifyWhatsapp?: boolean | undefined;
    notifyVoice?: boolean | undefined;
    recipients?: readonly string[] | undefined;
    whatsappContactIds?: readonly string[] | undefined;
    voiceContactIds?: readonly string[] | undefined;
    voiceWaitMinutes?: number | null | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  if (value.notifyEmail === true && (value.recipients?.length ?? 0) === 0) {
    ctx.addIssue({
      code: "custom",
      message: "Add at least one email address.",
      path: ["recipients"],
    });
  }

  if (value.notifyEmail === false && (value.recipients?.length ?? 0) > 0) {
    ctx.addIssue({
      code: "custom",
      message: "Email addresses are only accepted when email is selected.",
      path: ["recipients"],
    });
  }

  if (value.notifyWhatsapp === true && (value.whatsappContactIds?.length ?? 0) === 0) {
    ctx.addIssue({
      code: "custom",
      message: "Select at least one WhatsApp contact.",
      path: ["whatsappContactIds"],
    });
  }

  if (value.notifyWhatsapp === false && (value.whatsappContactIds?.length ?? 0) > 0) {
    ctx.addIssue({
      code: "custom",
      message: "WhatsApp contacts are only accepted when WhatsApp is selected.",
      path: ["whatsappContactIds"],
    });
  }

  if (value.notifyVoice === true && (value.voiceContactIds?.length ?? 0) === 0) {
    ctx.addIssue({
      code: "custom",
      message: "Select at least one contact to call.",
      path: ["voiceContactIds"],
    });
  }

  if (value.notifyVoice === true && (value.voiceWaitMinutes === undefined || value.voiceWaitMinutes === null)) {
    ctx.addIssue({
      code: "custom",
      message: "Say how many minutes to wait before calling.",
      path: ["voiceWaitMinutes"],
    });
  }

  if (value.notifyVoice === false && (value.voiceContactIds?.length ?? 0) > 0) {
    ctx.addIssue({
      code: "custom",
      message: "Call contacts are only accepted when calling is selected.",
      path: ["voiceContactIds"],
    });
  }

  if (value.notifyVoice === false && value.voiceWaitMinutes != null) {
    ctx.addIssue({
      code: "custom",
      message: "A call wait is only accepted when calling is selected.",
      path: ["voiceWaitMinutes"],
    });
  }
}

/**
 * Creating a rule by hand (ADR-092).
 *
 * At most one rule per metric for the whole organization (no `deviceId`) and
 * one per metric per device; a second one is a 409, because the engine would
 * pick one of them and ignore the other. Anything omitted starts from the
 * metric's entry in `DEFAULT_ALERT_RULES`, and recipients start empty.
 * Timer bounds are those of ADR-091; channel rules refuse timers.
 */
export const createAlertRuleRequestSchema = tenantScopeSchema
  .extend({
    metric: alertMetricSchema,
    /** Absent or null for a rule that covers every device in the organization. */
    deviceId: z.uuid().nullable().optional(),
    minValue: z.number().nullable().optional(),
    maxValue: z.number().nullable().optional(),
    durationSeconds: ruleDurationSchema.optional(),
    cooldownSeconds: ruleCooldownSchema.optional(),
    severity: severitySchema.optional(),
    enabled: z.boolean().optional(),
    recipients: recipientsSchema.optional(),
    ...channelSelectionSchema,
  })
  .superRefine(refineChannelSelection)
  .refine(
    (value) =>
      value.minValue === undefined ||
      value.maxValue === undefined ||
      value.minValue === null ||
      value.maxValue === null ||
      value.minValue < value.maxValue,
    { message: "The minimum must be below the maximum.", path: ["minValue"] },
  );

export type CreateAlertRuleRequest = z.infer<typeof createAlertRuleRequestSchema>;

/**
 * Fills in the default organization-wide rules an organization is missing
 * (ADR-092), notifying its active administrators as the seed does. Metrics
 * that already have one, enabled or not, are left alone, so calling it twice
 * creates nothing the second time.
 */
export const createDefaultAlertRulesRequestSchema = tenantScopeSchema;

export type CreateDefaultAlertRulesRequest = z.infer<typeof createDefaultAlertRulesRequestSchema>;

export const createDefaultAlertRulesResponseSchema = z.object({
  /** The metrics a rule was created for; empty when nothing was missing. */
  created: z.array(alertMetricSchema),
});

export type CreateDefaultAlertRulesResponse = z.infer<typeof createDefaultAlertRulesResponseSchema>;

/**
 * Editing a rule: recipients, timers, severity, enabled, and optional numeric
 * bounds (null bounds defer to the asset — ADR-048).
 *
 * There is no delete. Disabling a device's rule hands the device back to the
 * organization-wide one, and disabling that one silences the metric, which
 * covers what deleting would do without losing the incidents' rule.
 */
export const updateAlertRuleRequestSchema = z
  .object({
    recipients: recipientsSchema,
    enabled: z.boolean(),
    minValue: z.number().nullable(),
    maxValue: z.number().nullable(),
    durationSeconds: ruleDurationSchema,
    cooldownSeconds: ruleCooldownSchema,
    severity: severitySchema,
    ...channelSelectionSchema,
  })
  .partial()
  .superRefine(refineChannelSelection)
  .refine((value) => Object.keys(value).length > 0, {
    message: "Provide at least one field to update.",
  })
  // Only checked when both bounds arrive together. A patch touching one end
  // is validated against the stored other end, in the service, for the same
  // reason as the asset thresholds: "min below max" is a property of the
  // result rather than of the request.
  .refine(
    (value) =>
      value.minValue === undefined ||
      value.maxValue === undefined ||
      value.minValue === null ||
      value.maxValue === null ||
      value.minValue < value.maxValue,
    { message: "The minimum must be below the maximum.", path: ["minValue"] },
  );

export type UpdateAlertRuleRequest = z.infer<typeof updateAlertRuleRequestSchema>;

export const listAlertRulesQuerySchema = listQuerySchema.extend({
  metric: alertMetricSchema.optional(),
  deviceId: z.uuid().optional(),
  enabled: z.stringbool().optional(),
  /** Finds the rules nobody will hear about, which is the point of the screen. */
  withoutRecipients: z.stringbool().optional(),
});

export type ListAlertRulesQuery = z.infer<typeof listAlertRulesQuerySchema>;

export const alertRuleListSchema = paginatedSchema(alertRuleSchema);
export type AlertRuleList = z.infer<typeof alertRuleListSchema>;
